import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { Separator } from "../components/ui/separator";
import { Card, CardContent } from "../components/ui/card";
import { Truck, Globe, Clock, Package, Shield, MapPin } from "lucide-react";

const ShippingPolicyPage = () => {
  return (
    <div className="min-h-screen" data-testid="shipping-policy-page">
      <Navigation />
      
      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">Shipping Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>
          
          {/* Quick Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <Card>
              <CardContent className="pt-6 text-center">
                <Truck className="h-8 w-8 mx-auto mb-3 text-primary" />
                <h4 className="font-medium mb-1">Australia Wide</h4>
                <p className="text-sm text-muted-foreground">5-10 business days</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Globe className="h-8 w-8 mx-auto mb-3 text-primary" />
                <h4 className="font-medium mb-1">International</h4>
                <p className="text-sm text-muted-foreground">10-21 business days</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Shield className="h-8 w-8 mx-auto mb-3 text-primary" />
                <h4 className="font-medium mb-1">Fully Insured</h4>
                <p className="text-sm text-muted-foreground">All shipments protected</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-medium mb-4">1. Overview</h2>
              <p className="text-muted-foreground leading-relaxed">
                Art By Arawol ships authentic Aboriginal artwork from New South Wales, Australia to customers 
                worldwide. We take great care in packaging and shipping your artwork to ensure it arrives safely. 
                All shipments are fully insured.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">2. Processing Times</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Orders are typically processed within:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Prints:</strong> 2-3 business days</li>
                <li><strong>Framed Prints:</strong> 5-7 business days (custom framing required)</li>
                <li><strong>Original Artworks:</strong> 3-5 business days (specialist packaging)</li>
                <li><strong>Wholesale Orders:</strong> 7-14 business days depending on quantity</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Processing times may be extended during peak periods or for large orders. You will receive 
                email confirmation when your order ships.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">3. Australian Shipping</h2>
              
              <h3 className="text-xl font-medium mb-2 mt-6">Delivery Times</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Region</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Standard</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Express</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">NSW Metro</td>
                      <td className="py-3 px-4">3-5 business days</td>
                      <td className="py-3 px-4">1-2 business days</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">VIC, QLD, SA, ACT</td>
                      <td className="py-3 px-4">5-7 business days</td>
                      <td className="py-3 px-4">2-3 business days</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">WA, NT, TAS</td>
                      <td className="py-3 px-4">7-10 business days</td>
                      <td className="py-3 px-4">3-5 business days</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Remote Areas</td>
                      <td className="py-3 px-4">10-14 business days</td>
                      <td className="py-3 px-4">5-7 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-medium mb-2 mt-6">Shipping Costs</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Free Standard Shipping</strong> on orders over $500 AUD</li>
                <li>Standard shipping from $15 AUD for prints</li>
                <li>Original artworks quoted individually based on size and destination</li>
                <li>Express shipping available at checkout for additional cost</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">4. International Shipping</h2>
              
              <h3 className="text-xl font-medium mb-2 mt-6">Delivery Times</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Region</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Standard</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Express</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">New Zealand</td>
                      <td className="py-3 px-4">7-10 business days</td>
                      <td className="py-3 px-4">3-5 business days</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">Asia Pacific</td>
                      <td className="py-3 px-4">10-14 business days</td>
                      <td className="py-3 px-4">5-7 business days</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">USA & Canada</td>
                      <td className="py-3 px-4">14-21 business days</td>
                      <td className="py-3 px-4">7-10 business days</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4">UK & Europe</td>
                      <td className="py-3 px-4">14-21 business days</td>
                      <td className="py-3 px-4">7-10 business days</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Rest of World</td>
                      <td className="py-3 px-4">21-30 business days</td>
                      <td className="py-3 px-4">10-14 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-medium mb-2 mt-6">International Costs</h3>
              <p className="text-muted-foreground leading-relaxed">
                International shipping costs are calculated at checkout based on destination and package 
                dimensions. Costs typically range from $35-$150 AUD depending on location and order size.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">Customs & Import Duties</h3>
              <div className="p-4 bg-muted/50 rounded-xl mt-4">
                <p className="text-muted-foreground">
                  <strong>Important:</strong> International orders may be subject to customs duties, taxes, 
                  and fees imposed by the destination country. These charges are the responsibility of the 
                  buyer and are not included in our shipping charges. We recommend checking with your local 
                  customs office for applicable fees.
                </p>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">5. Packaging</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use specialized art shipping materials to ensure your artwork arrives in perfect condition:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Prints:</strong> Acid-free tissue, rigid cardboard mailers or tubes</li>
                <li><strong>Framed Works:</strong> Corner protectors, bubble wrap, double-walled boxes</li>
                <li><strong>Original Artworks:</strong> Custom wooden crates for high-value pieces</li>
                <li>All packages include Certificate of Authenticity</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">6. Insurance</h2>
              <p className="text-muted-foreground leading-relaxed">
                All shipments are fully insured for the purchase price. In the unlikely event of damage or 
                loss during transit, please contact us within 48 hours of delivery (or expected delivery date 
                for lost packages) to initiate a claim.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">7. Tracking</h2>
              <p className="text-muted-foreground leading-relaxed">
                All orders are shipped with full tracking. You will receive:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>Email confirmation when your order is dispatched</li>
                <li>Tracking number and link to track your shipment</li>
                <li>Estimated delivery date</li>
                <li>Delivery confirmation notification</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">8. Delivery Issues</h2>
              
              <h3 className="text-xl font-medium mb-2 mt-6">Undeliverable Packages</h3>
              <p className="text-muted-foreground leading-relaxed">
                If a package is returned to us as undeliverable due to an incorrect address or failure to 
                collect, you will be responsible for re-shipping costs.
              </p>

              <h3 className="text-xl font-medium mb-2 mt-6">Lost Packages</h3>
              <p className="text-muted-foreground leading-relaxed mt-4">
                If tracking shows delivered but you haven't received your order:
              </p>
              <ol className="list-decimal pl-6 text-muted-foreground space-y-2 mt-2">
                <li>Check with neighbors and household members</li>
                <li>Contact your local post office or courier depot</li>
                <li>Contact us within 7 days - we will investigate and arrange replacement or refund if necessary</li>
              </ol>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">9. Pickup Option</h2>
              <p className="text-muted-foreground leading-relaxed">
                Local pickup may be available for customers in the Greater Sydney area. Contact us to arrange 
                pickup and waive shipping costs. Pickup is by appointment only.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">10. Wholesale Shipping</h2>
              <p className="text-muted-foreground leading-relaxed">
                Wholesale customers benefit from:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>Discounted shipping rates for bulk orders</li>
                <li>Consolidated shipping for multiple items</li>
                <li>Priority processing</li>
                <li>Flexible delivery scheduling</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Contact us for a custom shipping quote on wholesale orders.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">11. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                For shipping questions or to track your order:
              </p>
              <div className="mt-4 p-4 bg-card rounded-xl border border-border">
                <p className="text-muted-foreground">
                  <strong>Art By Arawol Shipping</strong><br />
                  Email: shipping@arawol.art<br />
                  Include your order number in all correspondence
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShippingPolicyPage;
